import { Box, Text } from '@chakra-ui/react';
import { IOption, IProductOption, IVariant } from '../../../../../../../interfaces';
import Colors from '../../../../../../../config/colors';
import { useActions } from '../../../../../../../hooks/actions';
import { useAppSelector } from '../../../../../../../hooks/redux';
import {
    ChakraStylesConfig,
    Select,
    chakraComponents,
    GroupBase,
    SelectComponentsConfig,
} from 'chakra-react-select';
import { useTranslation } from 'react-i18next';
import { getFormatedPrice } from '../../../../../../../utils/price';
import Radio from '../../../../../../radio/Radio';
import { useLocaleType } from '../../../../../../../hooks/useLocaleType';

const chakraStyles: ChakraStylesConfig = {
    dropdownIndicator: (provided, { selectProps }) => ({
        ...provided,
        p: 0,
        w: '50px',
        background: Colors.darkBlue2,
        border: 'none',
        color: '#fff',
        '> svg': {
            transform: `rotate(${selectProps.menuIsOpen ? -180 : 0}deg)`,
        },
    }),
    control: (provided, state) => ({
        ...provided,
        borderBottomLeftRadius: state.menuIsOpen ? 0 : 'md',
        borderBottomRightRadius: state.menuIsOpen ? 0 : 'md',
        transitionDuration: 0,
        background: Colors.darkBlue2,
        border: 'none',
        color: '#fff',
        fontSize: '15px',
    }),
    menuList: (provided, state) => ({
        ...provided,
        transitionDuration: 0,
        background: Colors.darkBlue2,
        color: '#fff',
        border: 'none',
    }),
    option: (provided, state) => ({
        ...provided,
        transitionDuration: 0,
        background: Colors.darkBlue2,
        fontSize: '15px',
    }),
};

const customComponents: SelectComponentsConfig<any, true, GroupBase<any>> = {
    Option: ({ children, ...props }) => (
        <chakraComponents.Option {...props}>
            <Box
                padding="0px 6px"
                display="flex"
                justifyContent="space-between"
                width="100%"
                color={Colors.lightGrey2}>
                <Text maxWidth="250px">{children}</Text>
                <Text color="#fff">
                    {getFormatedPrice(
                        props.data.finalPrice,
                        props.data.localeType.currency,
                        props.data.localeType.name,
                    )}
                </Text>
            </Box>
        </chakraComponents.Option>
    ),
    MultiValueContainer: ({ children, ...props }) => (
        <chakraComponents.MultiValueContainer {...props}>
            {children}
        </chakraComponents.MultiValueContainer>
    ),
};

interface Props {
    variants: IVariant[];
    title: string;
    type: string;
}

export default function VariantList({ variants, title, type }: Props) {
    const [t, i18n] = useTranslation();
    const { localeType } = useLocaleType(i18n);

    const { setProductOptions } = useActions();
    const productOptions = useAppSelector((state) => state.productOptions.options);

    const selectVariant = (variant: IVariant) => {
        const currentProductOptions: IProductOption[] = [];

        const productOption = productOptions.find(
            (item) => item.localeType.name === localeType?.name,
        )!;

        const option = productOption.options.find((option: IOption) => {
            const currentVariant = option.variants.find((item) => item.id === variant.id);

            return currentVariant;
        })!;

        const variants: IVariant[] = option.variants;

        const currentVariants = variants?.map((item) => ({
            ...item,
            isDefault: item.id === variant.id,
        }));

        const currentOption = { ...option, variants: currentVariants };
        let updatedIndex = null;
        const currentOptions = productOption.options.map((item, index) => {
            const isUpdated = item.id === option.id;
            if (isUpdated) {
                updatedIndex = index;
            }
            return isUpdated ? currentOption : item;
        });

        currentProductOptions.push({ ...productOption, options: currentOptions });

        for (const currentProductOption of productOptions) {
            if (currentProductOption.localeType.name !== localeType?.name) {
                const options = [];
                let curIndex = 0;

                for (const item of currentProductOption.options) {
                    if (curIndex === updatedIndex) {
                        const variants = item.variants.map((variant, index) => ({
                            ...variant,
                            isDefault: currentVariants[index]?.isDefault,
                        }));

                        options.push({ ...item, variants });
                    } else {
                        options.push(item);
                    }
                    curIndex++;
                }

                currentProductOptions.push({ ...currentProductOption, options });
            }
        }

        setProductOptions({ options: currentProductOptions });
    };

    const variantList = variants?.map((variant: IVariant) => ({
        label: variant.name,
        value: variant.id,
        finalPrice: variant.finalPrice,
        isDefault: variant.isDefault,
        localeType,
    }));

    const currentVariant = variantList.find((item) => item.isDefault);

    return (
        <Box display="flex" flexDirection="column" gap="3">
            <Text color={Colors.lightGrey2}>{title}</Text>
            <Box>
                {type === 'radio' &&
                    variants.map((variant) => (
                        <Radio
                            value={variant.id}
                            name={title}
                            checked={variant.isDefault}
                            onClick={() => selectVariant(variant)}
                            children={
                                <p
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                    }}>
                                    <span
                                        style={{
                                            fontWeight: '500',
                                            color: Colors.lightGrey2,
                                            maxWidth: '220px',
                                            fontSize: '15px',
                                        }}>
                                        {variant.name}
                                    </span>
                                    <span
                                        style={{
                                            display: 'flex',
                                            fontWeight: '500',
                                            color: '#fff',
                                            fontSize: '15px',
                                        }}>
                                        {`+ ${getFormatedPrice(
                                            variant.finalPrice,
                                            localeType?.currency ?? '',
                                            localeType?.name ?? '',
                                        )} `}
                                    </span>
                                </p>
                            }
                        />
                    ))}

                {type === 'select' && (
                    <Select
                        isSearchable={false}
                        onChange={(option: any) =>
                            selectVariant(variants.find((item) => item.id === option.value)!)
                        }
                        options={variantList}
                        value={currentVariant}
                        chakraStyles={chakraStyles}
                        components={customComponents}
                    />
                )}
            </Box>
        </Box>
    );
}
